import React, { useCallback } from 'react';

import { Helpers } from '../core/src/helpers';
import { transformProduct } from '../search/util';
import useSiteConfig from '../hooks/useSiteConfig';

/**
 *
 * @returns {function} - A function that takes in an array Hawksearch results and returns an array of transformed products
 */
export const useTransformProducts = () => {
    const siteConfig = useSiteConfig();
    const { basename } = siteConfig || {};
    const fn = useCallback(
        results =>
            results.map(doc =>
                transformProduct(doc.Document, basename, { includeRest: true, optimizeImage: true, pickImage: true }),
            ),
        [basename],
    );
    return fn;
};

const distilString = (str = '') => str.trim().replace(/ /g, '_').toLowerCase();

const inGroup = (product, group = '') => {
    const { prodsubgrp, pm_producttype } = product;

    // Special case for Multi-packs
    if (prodsubgrp === 'Multi-packs' && group?.['mapping-rule']) {
        const subGroup = group['mapping-rule']?.[0]?.filter_subgrp;
        return subGroup === product?.filter_subgrp;
    }

    // Special case for Accessories
    if (!group.prodsubgrp && group.pm_producttype) {
        // check if value is defined and check if it matches the group
        if (pm_producttype) return pm_producttype.includes(group.pm_producttype);
        // pm_producttype on accessory return false
        return false;
    }

    const groups = Array.isArray(prodsubgrp) ? prodsubgrp : [prodsubgrp];

    // loops through prodsubgrp and returns true is any of the prodsubgrp matches the group
    return groups.reduce(
        (acc, subGroup = '') => distilString(subGroup) === distilString(group.prodsubgrp) || acc,
        false,
    );
};

export const mapResults = Helpers.memoize(
    (searhTerm, productGroups, results, testGroup = false) => {
        let skus = [...results];

        const mapping = [
            ...productGroups,
            ...((testGroup && [
                {
                    ProductSubGroup: [{ prodsubgrp: 'ALL', desc: 'ALL', violator: 'Message' }],
                    columns: [{ ALL_DATA: '' }],
                },
            ]) ||
                []),
        ].reduce((gacc, group) => {
            const { ProductSubGroup = [], title, columns, ...rest } = group;
            const groups = ProductSubGroup.reduce((acc, subGroup) => {
                const products = results.filter(product => inGroup(product, subGroup));

                products.forEach(product => {
                    skus = skus.filter(sku => sku.sku !== product.sku);
                });

                if (products.length > 0) {
                    acc.push({ ...subGroup, products });
                }
                return acc;
            }, []);

            if (groups.length > 0) {
                gacc.push({
                    title,
                    columns,
                    groups,
                    ...rest,
                });
            }

            return gacc;
        }, []);

        return mapping;
    },
    (searhTerm, productGroups, results = []) => `${searhTerm}-${!!productGroups}-${results.length}`,
);

export const cleanName = name => {
    if (!name) return '';
    return name.toLowerCase().replaceAll('refurbished', '').replaceAll(' ', '').trim();
};

export const autoCompleteAction = (data, query, onAutocompleteSelect, setResults) => {
    const isMatch = data?.Products?.filter(p => {
        const sku = p?.Sku.toLowerCase();
        const term = query.toLowerCase();
        return sku === term || Helpers.removeSKULocalization(sku) === term;
    });

    if (isMatch.length === 1) {
        return onAutocompleteSelect(isMatch?.[0]?.Sku);
    }

    const firstResult = cleanName(data?.Products?.[0]?.Results?.Document?.product_name?.[0]);
    const allMatchByName = data?.Products?.every(p => {
        const name = cleanName(p?.Results?.Document?.product_name?.[0]);
        return name === firstResult;
    });

    if (allMatchByName) {
        const mainProduct =
            data?.Products?.find(p => p?.Results?.Document?.thumbnailimage?.[0] !== 'null') || data?.Products?.[0];

        return onAutocompleteSelect(mainProduct?.Sku, {
            skuOverride: data.Products.length > 1 && data?.Products?.map(p => p?.Sku).join(', '),
        });
    }

    const commonIds = data?.Products?.map(p =>
        p?.Results?.Document?.selectability?.[0]
            ?.toLowerCase()
            ?.split('/')
            ?.map(s => s.replace(/\D/g, '')),
    );
    const allMatch = commonIds
        ?.sort((a, b) => b.length - a.length)
        ?.slice(1)
        ?.every(arr => arr?.every(id => commonIds?.[0]?.includes(id)));
    // its ink family search if there is a common id among the results
    if (allMatch) {
        return onAutocompleteSelect(query);
    }

    const paperTerm = (data?.Products || []).every(p => p?.Results?.Document?.prnsuptype?.[0] === 'paper') ? query : '';
    if (paperTerm) {
        return onAutocompleteSelect(paperTerm);
    }

    const isSinglePrinter =
        data?.Products.length === 1 &&
        (data?.Products[0]?.Results?.Document?.product_type?.[0]?.toLowerCase() === 'printer' ||
            data?.Products[0]?.Results?.Document?.product_type?.[0]?.toLowerCase() === 'printers');

    if (isSinglePrinter) {
        return onAutocompleteSelect(data?.Products[0]?.Results?.Document?.sku?.[0]);
    }

    return setResults({ Products: data.Products });
};

export const hawkPayloadToResults = (data, query, productGroups) => {
    const transformedResults =
        data?.Results?.map(r =>
            transformProduct(r.Document, undefined, { includeRest: true, optimizeImage: true, pickImage: true }),
        ) || [];
    const printers = transformedResults?.filter(p => p.product_type === 'Printer');

    const supplies = mapResults(query, productGroups, transformedResults || []) || [];

    return { printers, supplies };
};
